import { aiAccessRefusalMessage } from './aiAccess'

const isHtmlContent = (content: any): boolean => {
  if (typeof content !== 'string') return false
  const trimmed = content.trim().toLowerCase()
  return trimmed.startsWith('<!doctype') || trimmed.startsWith('<html')
}

const getNetworkErrorMessage = (status?: number): string => {
  if (status === 520) return 'The server returned an unexpected response. Please try again.'
  if (status === 521) return 'The server is currently unreachable. Please try again later.'
  if (status === 522) return 'Connection timed out. Please try again.'
  if (status === 523) return 'The server is unreachable. Please try again later.'
  if (status === 524) return 'The request timed out. Please try again.'
  if (status === 525) return 'SSL handshake failed. Please try again.'
  if (status === 526) return 'Invalid SSL certificate. Please contact support.'
  if (status === 502) return 'Bad gateway. The server is temporarily unavailable.'
  if (status === 503) return 'Service unavailable. Please try again later.'
  if (status === 504) return 'Gateway timeout. Please try again.'
  return 'A network error occurred. Please try again.'
}

export const extractErrorMessage = (error: any): string => {
  if (!error) return ''

  // An AI access refusal (#1537) reads the same wherever it surfaces.
  const aiAccess = aiAccessRefusalMessage(error)
  if (aiAccess) return aiAccess

  let errorMessage = ''

  if(error.response && error.response.data) {
    // Check if response is HTML (e.g., Cloudflare error page)
    if (isHtmlContent(error.response.data)) {
      return getNetworkErrorMessage(error.response.status)
    }
    // Prefer 'details' field as it often contains more specific error info
    if (error.response.data.details) {
      errorMessage = error.response.data.details as string
    }
    else if (error.response.data.message || error.response.data.error) {
      errorMessage = (error.response.data.message || error.response.data.error) as string
    }
    else if (error.response.data) errorMessage = typeof error.response.data === 'string' ? error.response.data : JSON.stringify(error.response.data)
    else errorMessage = error.toString()
  }
  else if(error.error) {
    errorMessage = error.error
  }
  else if(error.message) {
    errorMessage = error.message
  }
  else {
    errorMessage = JSON.stringify(error)
  }

  // Try to extract embedded JSON error messages
  // Pattern: "Failed to create user: HTTP error 500: {\"code\":902,\"message\":\"...\"}"
  if (typeof errorMessage === 'string' && errorMessage.includes('{')) {
    try {
      // Find the JSON part (from first { to end)
      const jsonStartIndex = errorMessage.indexOf('{')
      if (jsonStartIndex !== -1) {
        const jsonString = errorMessage.substring(jsonStartIndex)
        // Try to parse it
        const parsed = JSON.parse(jsonString)
        // If it has a message field, use that
        if (parsed.message && typeof parsed.message === 'string') {
          return parsed.message
        }
      }
    } catch (e) {
      // If parsing fails, just return the original message
    }
  }

  return errorMessage
}

/**
 * The machine-readable `code` field a structured server error carries
 * alongside `error`/`message` (e.g. `regenerate_not_permitted`, #1219 gate 2).
 * Undefined for anything that isn't an axios-shaped error with a JSON body
 * carrying a string `code`.
 */
export const extractErrorCode = (error: any): string | undefined => {
  const data = error?.response?.data
  if (data && typeof data === 'object' && typeof data.code === 'string') {
    return data.code
  }
  return undefined
}

